import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  Badge,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  People,
  School,
  Assignment,
  Analytics,
  Settings,
  Person,
  AssignmentTurnedIn,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings,
  Schedule,
  BarChart,
  Book,
  AccountTree,
  Storage,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { NavItem } from '../../types';

const navigationItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'Dashboard',
    path: '/dashboard',
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: 'AdminPanelSettings',
    path: '/admin',
    roles: ['admin'],
    children: [
      {
        id: 'admin-users',
        label: 'User Management',
        icon: 'People',
        path: '/admin/users',
        roles: ['admin'],
      },
      {
        id: 'admin-classes',
        label: 'Class Management',
        icon: 'School',
        path: '/admin/classes',
        roles: ['admin'],
      },
      {
        id: 'admin-subjects',
        label: 'Subject Management',
        icon: 'Book',
        path: '/admin/subjects',
        roles: ['admin'],
      },
      {
        id: 'admin-faculty-subject-assignment',
        label: 'Faculty Assignment',
        icon: 'AccountTree',
        path: '/admin/faculty-subject-assignment',
        roles: ['admin'],
      },
      {
        id: 'admin-data-migration',
        label: 'Data Migration',
        icon: 'Storage',
        path: '/admin/data-migration',
        roles: ['admin'],
      },
      {
        id: 'admin-subject-attendance',
        label: 'Subject Attendance',
        icon: 'BarChart',
        path: '/admin/subject-attendance',
        roles: ['admin'],
      },
    ],
  },
  {
    id: 'faculty',
    label: 'Faculty',
    icon: 'Person',
    path: '/faculty',
    roles: ['faculty', 'admin'],
    children: [
      {
        id: 'faculty-attendance',
        label: 'Class Attendance',
        icon: 'AssignmentTurnedIn',
        path: '/faculty/attendance',
        roles: ['faculty', 'admin'],
      },
      {
        id: 'faculty-subject-attendance',
        label: 'Mark Subject Attendance',
        icon: 'Schedule',
        path: '/faculty/subject-attendance',
        roles: ['faculty', 'admin'],
      },
      {
        id: 'faculty-subject-attendance-view',
        label: 'View Subject Attendance',
        icon: 'BarChart',
        path: '/faculty/subject-attendance-view',
        roles: ['faculty', 'admin'],
      },
      {
        id: 'faculty-student-subject-attendance',
        label: 'Student Subject Reports',
        icon: 'Assignment',
        path: '/faculty/student-subject-attendance',
        roles: ['faculty', 'admin'],
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'Assignment',
    path: '/reports',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'Analytics',
    path: '/analytics',
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'Settings',
    path: '/settings',
  },
];

const iconMap: Record<string, React.ReactElement> = {
  Dashboard: <Dashboard />,
  People: <People />,
  School: <School />,
  Assignment: <Assignment />,
  Analytics: <Analytics />,
  Settings: <Settings />,
  Person: <Person />,
  AssignmentTurnedIn: <AssignmentTurnedIn />,
  AdminPanelSettings: <AdminPanelSettings />,
  Schedule: <Schedule />,
  BarChart: <BarChart />,
  Book: <Book />,
  AccountTree: <AccountTree />,
  Storage: <Storage />,
};

interface SidebarItemProps {
  item: NavItem;
  level?: number;
}

function SidebarItem({ item, level = 0 }: SidebarItemProps) {
  const theme = useTheme();  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);

  // Check if user has permission to view this item
  const hasPermission = !item.roles || (user && item.roles.includes(user.role));

  const isActive = location.pathname === item.path || 
    (item.children && item.children.some(child => location.pathname === child.path));
  const isCurrentPage = location.pathname === item.path;

  const handleClick = () => {
    if (item.children) {
      setOpen(!open);
    } else {
      navigate(item.path);
    }
  };

  React.useEffect(() => {
    if (isActive && item.children) {
      setOpen(true);
    }
  }, [isActive, item.children]);

  if (!hasPermission) {
    return null;
  }

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleClick}
          selected={isCurrentPage}
          sx={{
            pl: 2 + level * 2,
            py: 1,
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
            '&:hover': {
              bgcolor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              color: isCurrentPage ? 'inherit' : 'text.secondary',
            }}
          >
            {iconMap[item.icon] || <Dashboard />}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isCurrentPage ? 600 : 400,
            }}
          />
          {item.children && (
            <>
              {item.id === 'admin-users' && (
                <Badge badgeContent={12} color="info" sx={{ mr: 1 }} />
              )}
              {open ? <ExpandLess /> : <ExpandMore />}
            </>
          )}
        </ListItemButton>
      </ListItem>

      {item.children && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child) => (
              <SidebarItem key={child.id} item={child} level={level + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export function Sidebar() {
  const theme = useTheme();
  const { user } = useAuth();

  const filteredNavItems = navigationItems.filter(item => 
    !item.roles || (user && item.roles.includes(user.role))
  );

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      {/* Logo/Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.contrastText',
          }}
        >
          <School />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            AttendEase
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Smart Attendance System
          </Typography>
        </Box>
      </Box>

      {/* User Info */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.contrastText',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {user?.username?.[0]?.toUpperCase() || 'U'}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.username}
            </Typography>            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        <List>
          {filteredNavItems.map((item) => (
            <SidebarItem key={item.id} item={item} />
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center">
          Version 2.0.0
        </Typography>
      </Box>
    </Box>
  );
} 